/**
 * BabyShowerPlayfulBash — Playful interactive baby shower invitation.
 *
 * Features: Cute animations, interactive games, guest predictions board,
 * gender-neutral design, Marathi/English bilingual support.
 *
 * id: `baby-shower-playful-bash`
 */

import React, { useState, useMemo } from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { HeroBabySection } from './sections/HeroBabySection';
import { CountdownBabySection } from './sections/CountdownBabySection';
import { GuessPredictionsSection } from './sections/GuessPredictionsSection';
import { BabyGamesSection } from './sections/BabyGamesSection';
import { VenueBabySection } from './sections/VenueBabySection';
import { RSVPBabySection } from './sections/RSVPBabySection';
import { ParticleBackground } from '../BirthdayNeonBash/sections/ParticleBackground';
import './baby-shower-playful.css';

const META: TemplateMetadata = {
  id: 'baby-shower-playful-bash',
  name: 'Baby Shower: Playful Bash',
  occasionType: 'BABY_SHOWER',
  description: 'Playful interactive baby shower invitation with cute animations, guest predictions game, fun interactive games, and gender-neutral design.',
  isPremium: true,
  tags: ['baby-shower', 'playful', 'cute', 'interactive', 'games', 'gender-neutral', 'premium'],
  colorScheme: {
    primary: '#FFD60A',
    accent: '#00D9FF',
    bg: '#FFF8F0',
  },
};

export function BabyShowerPlayfulBash({ data }: { data: InvitationData }) {
  const customDataAny = (data.customData || {}) as any;
  const babyData = customDataAny.baby || {};
  const [language, setLanguage] = useState<'en' | 'mr'>('en');
  const [predictionsCount, setPredictionsCount] = useState(0);

  const content = useMemo(() => {
    const babyName = babyData.babyName || 'Baby';
    const babyNameMr = babyData.babyNameMr || babyName;
    const dueDate = data.eventDate ? new Date(data.eventDate) : new Date();
    const venueName = data.eventVenue || 'Party Venue';
    const venueNameMr = babyData.eventVenueMr || venueName;

    return {
      baby: {
        name: babyName,
        nameMr: babyNameMr,
        dueDate,
        photo: babyData.photo || data.mediaUrls?.[0] || 'https://images.unsplash.com/photo-1515488846076-7ad8d5e1d08f?auto=format&fit=crop&w=500',
        parentNames: babyData.parentNames || 'Expecting Parents',
        parentNamesMr: babyData.parentNamesMr || 'आशा करणारे माता-पिता',
      },
      event: {
        date: dueDate,
        dateStr: dueDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        dateMr: dueDate.toLocaleDateString('mr-IN', { weekday: 'long', month: 'short', day: 'numeric' }),
        time: data.eventTime || '3:00 PM',
        venue: venueName,
        venueMr: venueNameMr,
        message: babyData.message || 'Join us for a special celebration!',
        messageMr: babyData.messageMr || 'आमच्याबरोबर विशेष उत्सवमध्ये सामील व्हा!',
      },
      invitationId: data.invitationId ?? data.id ?? null,
      isLive: data.status === 'PUBLISHED' || data.status === 'PROCESSING',
    };
  }, [data, babyData]);

  return (
    <div className="baby-shower-playful min-h-screen bg-gradient-to-b from-yellow-50 via-blue-50 to-pink-50 text-gray-800 overflow-hidden">
      <ParticleBackground />

      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => setLanguage('en')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'en'
              ? 'bg-blue-400 text-white shadow-lg shadow-blue-400'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          EN
        </button>
        <button
          onClick={() => setLanguage('mr')}
          className={`px-4 py-2 rounded-full font-bold transition-all ${
            language === 'mr'
              ? 'bg-pink-400 text-white shadow-lg shadow-pink-400'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          मराठी
        </button>
      </div>

      <main className="relative z-10">
        <HeroBabySection content={content} language={language} />
        <CountdownBabySection dueDate={content.baby.dueDate} language={language} />
        <GuessPredictionsSection
          invitationId={content.invitationId}
          isLive={content.isLive}
          language={language}
          onPredictionAdded={() => setPredictionsCount(p => p + 1)}
          totalPredictions={predictionsCount}
        />
        <BabyGamesSection language={language} />
        <VenueBabySection venue={content.event} language={language} />
        <RSVPBabySection invitationId={content.invitationId} isLive={content.isLive} language={language} />
      </main>

      {/* Footer */}
      <div className="relative z-10 text-center py-12 text-gray-500 text-sm">
        <p>👶 {language === 'mr' ? 'नवजातकाचे स्वागत' : 'Welcoming a new little one!'}</p>
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: BabyShowerPlayfulBash });
